import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Modal as RACModal, ModalOverlay } from "react-aria-components";
import { cn } from "@/utils/helpers";
import { KitButton } from "./kit/Button";
import { KitInput } from "./kit/Input";
import { KitTextarea } from "./kit/Textarea";
import { useCoShellPortal } from "../CoShellPortalContext";
import { takeFirstBrandWebsite } from "@/components/gettingIn/FirstRun";

/**
 * First-run onboarding for an empty Brand Universe — a full-bleed takeover over
 * the cockpit, shown whenever the universe has fewer than 3 facts. Entirely
 * optional (Esc / ✕ / "Skip for now" all leave). On submit it turns the answers
 * into a first-person message and sends it to the universe chat, as if the user
 * asked the AI to bootstrap their universe.
 *
 * The staging is the point: an orbit field forms as you answer (inner ring on
 * arrival, core ignites once the website reads as a URL, outer ring on step 2,
 * flare on submit), so the universe is visibly assembling around the answers
 * rather than waiting behind a form. Same sun-and-rings language as the stage's
 * own Assembly intro.
 *
 * Two conventions worth knowing before editing:
 *  - RAC's ModalOverlay owns focus containment, scroll lock and Esc; every
 *    visual transition is motion/react. The cockpit ships NO tailwindcss-animate
 *    plugin, so `animate-in`-style classes are silently inert here.
 *  - Entrance timing is explicit per-element delay, NOT variants/staggerChildren.
 *    The headline's lines are nested inside <h1>/mask spans, and leaning on
 *    variant propagation through those wrappers is how the brand name silently
 *    got stuck at opacity 0 once already.
 */

const INDUSTRIES = [
  "E-commerce",
  "Fashion & apparel",
  "Beauty & cosmetics",
  "Food & beverage",
  "Health & wellness",
  "Fitness",
  "Technology / SaaS",
  "Finance & fintech",
  "Real estate",
  "Travel & hospitality",
  "Education",
  "Entertainment & media",
  "Gaming",
  "Automotive",
  "Home & furniture",
  "Consumer electronics",
  "Marketing & advertising",
  "Nonprofit",
  "Professional services",
  "Manufacturing",
  "Retail",
  "Sports",
  "Art & design",
  "Music",
  "Restaurants & cafés",
  "Luxury goods",
  "Pets",
  "Kids & baby",
  "Sustainability",
];

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];
const EASE_IN: [number, number, number, number] = [0.7, 0, 0.84, 0];
/** Long enough for the flythrough to read as a hand-off; short enough to never gate the chat. */
const EXIT_MS = 560;

/* Onboarding fields are the library Input / Input · Multiline (Figma 8 Oct):
   the question is the field's label. */

function normalizeSite(raw: string): string {
  const s = raw.trim();
  if (!s) return s;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

function hostOf(raw: string): string {
  return raw.trim().replace(/^https?:\/\//i, "").split(/[/?#]/)[0].trim();
}

function shorten(raw: string, max: number): string {
  const t = raw.replace(/\s+/g, " ").trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Answers seed orbiting facts, so the value has to settle before a chip flies
 * out — without this a planet spawns on the "B" of "Beauty" and relabels on
 * every keystroke.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * Deliberately loose: this only catches "that isn't a website at all" (a bare
 * word, a sentence) so the AI isn't handed a link it can't research. Judging
 * real TLDs is the researcher's job, not the form's.
 */
function siteError(raw: string): string | null {
  const s = raw.trim();
  if (!s) return "We need a website to research your brand.";
  const host = s
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/)[0]
    .trim();
  if (!/^[^\s.]+(\.[^\s.]+)+$/.test(host)) return "That doesn't look like a website address yet.";
  return null;
}

function buildMessage(opts: {
  brandName?: string;
  website: string;
  industry: string;
  about: string;
}): string {
  const { brandName, website, industry, about } = opts;
  const sentences: string[] = [];
  sentences.push(brandName?.trim() ? `My brand is ${brandName.trim()}.` : "I'm setting up my brand.");
  sentences.push(`Our website is ${normalizeSite(website)}.`);
  if (industry.trim()) sentences.push(`We operate in the ${industry.trim()} space.`);
  if (about.trim()) sentences.push(`Let me tell you a bit about us: ${about.trim()}`);
  return (
    `Help me bootstrap my brand universe.\n\n${sentences.join(" ")}\n\n` +
    `Please look into the brand and propose the foundational facts (identity, audience, ` +
    `positioning, and anything else you can ground in what you find) to build out our universe.\n\n` +
    `Propose at least 20 foundational facts.`
  );
}

/* ------------------------------------------------------------------ motion */

/**
 * A line of type rising out of a mask.
 *
 * The padding/negative-margin pairs are load-bearing, not cosmetic. The headline
 * runs at leading-[0.95], so the line box is SHORTER than the glyphs: bare
 * `overflow-hidden` shears the caps off the top and the descenders (a "y" in a
 * brand name) off the bottom. The padding grows the clip box past the glyphs and
 * the negative margins take the layout cost back out, so the mask hides only
 * what's below the line. `y` must then start past 100% + the bottom padding, or
 * the top of the text peeks out before the reveal starts.
 */
function RiseLine({
  children,
  delay,
  reduce,
  className,
}: {
  children: ReactNode;
  delay: number;
  reduce: boolean;
  className?: string;
}) {
  return (
    <span className="block overflow-hidden pt-[0.18em] -mt-[0.18em] pb-[0.28em] -mb-[0.28em]">
      <motion.span
        className={cn("block", className)}
        initial={reduce ? { opacity: 0 } : { y: "145%" }}
        animate={reduce ? { opacity: 1 } : { y: "0%" }}
        transition={{ duration: reduce ? 0.001 : 0.95, ease: EASE_OUT, delay: reduce ? 0 : delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/** Explicit entrance props — see the file header on why this isn't variants. */
function rise(delay: number, reduce: boolean, distance = 18) {
  return {
    initial: { opacity: 0, y: reduce ? 0 : distance },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0.001 : 0.6, ease: EASE_OUT, delay: reduce ? 0 : delay },
  } as const;
}

/* ------------------------------------------------------------- orbit field */

/**
 * The orbit field is the product demo, not decoration: every answer seeds a real
 * labelled fact into orbit, so by the time you submit you've already watched
 * three facts appear and the pitch ("this is what a universe is made of") has
 * made itself. The rings therefore start EMPTY on purpose — an empty universe is
 * the thing the onboarding exists to fix, so showing it is the setup.
 *
 * BOX is a fixed pixel square rather than a fluid one: seeds animate along a
 * polar path in px (motion's x/y), and those numbers have to agree with the SVG
 * viewBox. Making the box responsive would desync the two coordinate spaces.
 */
/* Universe / Orbit, as the onboarding draws it (842 x 900, the frame at
   x639 y140 of the 1440 x 1180 desk): the five resting rings, tilted so their
   centres climb towards the sun, and the brand's sun at (433, 410). */
const ORBIT_W = 842;
const ORBIT_H = 900;
const SUN = { x: 433, y: 410 };
const RINGS = [
  { cx: 432.5, cy: 410, rx: 109.5, ry: 85 },
  { cx: 426.5, cy: 422, rx: 181.5, ry: 143 },
  { cx: 420.5, cy: 433, rx: 253.5, ry: 203 },
  { cx: 414.5, cy: 445, rx: 325.5, ry: 263.5 },
  { cx: 409.5, cy: 458, rx: 397.5, ry: 326 },
];
/** Where each answer sits on the orbit (top-left of its pill, in orbit space). */
const SEED_AT: Record<string, { x: number; y: number }> = {
  site: { x: 453, y: 176 },
  industry: { x: 173, y: 312 },
  about: { x: 560, y: 712 },
};

type Seed = { key: string; label: string };

/** Answer · Tag / Pill: a live dot and the answer in accent ink. */
function OrbitSeed({ seed, reduce }: { seed: Seed; reduce: boolean }) {
  const at = SEED_AT[seed.key] ?? SEED_AT.about;
  return (
    <motion.div
      className="absolute flex h-6 items-center gap-[7px] whitespace-nowrap rounded-tag px-2"
      style={{ left: at.x, top: at.y, background: "color-mix(in srgb, var(--co-accent-dim) 12%, transparent)" }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: reduce ? 0.001 : 0.45, ease: EASE_OUT }}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-co-accent" />
      <span className="max-w-[220px] truncate font-body text-[11px] font-semibold uppercase tracking-[0.9px] text-co-accent-ink">
        {seed.label}
      </span>
    </motion.div>
  );
}

/** The orbit's frame on the desk, placed against the viewport's middle as drawn. */
const ORBIT_FRAME_STYLE = {
  left: "calc(50% - 81px)",
  top: "calc(50% - 450px)",
  width: ORBIT_W,
  height: ORBIT_H,
} as const;
const ORBIT_FRAME = "pointer-events-none absolute hidden lg:block";

/** Flying through the sun on submit: a disc that swallows the viewport. */
function FlareBurst({ active, reduce }: { active: boolean; reduce: boolean }) {
  return (
    <motion.span
      className="absolute block rounded-full bg-co-accent"
      style={{ left: SUN.x - 10, top: SUN.y - 10, width: 20, height: 20, filter: "drop-shadow(0 0 40px var(--co-accent-glow))" }}
      initial={false}
      animate={active ? { scale: 240, opacity: [1, 1, 0] } : { scale: 0, opacity: 0 }}
      transition={{ duration: reduce ? 0.001 : EXIT_MS / 1000, ease: EASE_IN, times: [0, 0.55, 1] }}
    />
  );
}

/**
 * The empty orbit the answers land in. `level` 3 is the submit: the field
 * clears while FlareBurst flies through the sun into the real stage.
 */
function OrbitField({ level, seeds, initial, reduce }: { level: 0 | 1 | 2 | 3; seeds: Seed[]; initial: string; reduce: boolean }) {
  const flare = level === 3;
  return (
    <motion.div
      className="relative"
      style={{ width: ORBIT_W, height: ORBIT_H }}
      animate={{ opacity: flare ? 0 : 1 }}
      transition={{ duration: reduce ? 0.001 : flare ? 0.26 : 0.4, ease: flare ? EASE_IN : EASE_OUT }}
    >
      <svg viewBox={`0 0 ${ORBIT_W} ${ORBIT_H}`} className="absolute inset-0 h-full w-full" fill="none" aria-hidden>
        {RINGS.map((ring, i) => (
          <motion.ellipse
            key={ring.rx}
            cx={ring.cx}
            cy={ring.cy}
            rx={ring.rx}
            ry={ring.ry}
            stroke="var(--universe-arc)"
            strokeWidth={1}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{
              pathLength: { duration: reduce ? 0.001 : 1.5, ease: EASE_OUT, delay: reduce ? 0 : 0.2 + i * 0.1 },
              opacity: { duration: reduce ? 0.001 : 0.6, ease: EASE_OUT },
            }}
          />
        ))}
      </svg>
      {/* The sun: the brand's initial on the accent disc, 48 across. */}
      <motion.span
        className="absolute grid size-12 place-items-center rounded-full bg-co-accent font-section text-[20px] font-extrabold leading-none text-co-on-accent"
        style={{ left: SUN.x - 24, top: SUN.y - 24, boxShadow: "0 0 16px rgba(var(--co-accent-rgb),.32)" }}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduce ? 0.001 : 0.7, ease: EASE_OUT, delay: reduce ? 0 : 0.35 }}
      >
        {initial}
      </motion.span>

      <AnimatePresence>
        {seeds.map((seed) => (
          <OrbitSeed key={seed.key} seed={seed} reduce={reduce} />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

/* -------------------------------------------------------------- combobox */

/** Type-anything autocomplete: suggestions filter the list but a custom value is fine. */
function IndustryCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const reduce = !!useReducedMotion();

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    const list = q ? INDUSTRIES.filter((i) => i.toLowerCase().includes(q)) : INDUSTRIES;
    return list.slice(0, 8);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Hide the list once the input already exactly matches its only suggestion.
  const exact = value.trim().toLowerCase();
  const showList =
    open && suggestions.length > 0 && !(suggestions.length === 1 && suggestions[0].toLowerCase() === exact);

  return (
    <div ref={rootRef} className="relative">
      <KitInput
        id="onb-industry"
        label="Industry"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          // Esc closes the list, not the whole takeover — stop RAC from seeing it.
          if (e.key === "Escape" && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        placeholder="e.g. Beauty & cosmetics, or type your own"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showList}
      />
      <AnimatePresence>
        {showList && (
          <motion.ul
            initial={{ opacity: 0, y: reduce ? 0 : -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduce ? 0 : -6 }}
            transition={{ duration: reduce ? 0.001 : 0.16, ease: EASE_OUT }}
            className="co-glass-chrome absolute z-10 mt-1.5 max-h-56 w-full overflow-y-auto rounded-card p-1.5"
          >
            {suggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(s);
                    setOpen(false);
                  }}
                  className={cn(
                    "block w-full rounded-row px-3 py-1.5 text-left text-[13px] text-co-text2 transition-colors hover:bg-co-accent-dim hover:text-co-accent-ink",
                    s.toLowerCase() === exact && "bg-co-accent-dim text-co-accent-ink",
                  )}
                >
                  {s}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ main */

export default function UniverseOnboarding({
  brandName,
  onClose,
  onSubmit,
}: {
  /** The RESOLVED brand name, or undefined while it loads — never a placeholder. */
  brandName?: string;
  onClose: () => void;
  /** Receives the composed first-person message to hand to the universe chat. */
  onSubmit: (message: string) => void;
}) {
  // Portal into .co-shell so --co-* tokens resolve inside the overlay (RAC
  // otherwise portals to document.body, outside the token scope).
  const portalContainer = useCoShellPortal();
  const reduce = !!useReducedMotion();

  const [step, setStep] = useState<0 | 1>(0);
  // The website typed on the first-brand page (Figma 10 · First run 01), once.
  const [website, setWebsite] = useState(takeFirstBrandWebsite);
  const [industry, setIndustry] = useState("");
  const [about, setAbout] = useState("");
  // Held back until the first Continue attempt: validating as they type would
  // shout "not a website" at someone three characters into a URL.
  const [error, setError] = useState<string | null>(null);
  // The takeover plays its own leave, THEN hands back to the parent (which
  // unmounts it). RAC can't do this for us: its exit hook watches for CSS
  // animations, and this project ships no animation plugin.
  const [leaving, setLeaving] = useState<null | "skip" | "submit">(null);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);

  const leave = (mode: "skip" | "submit", done: () => void) => {
    if (reduce) return done();
    setLeaving(mode);
    timerRef.current = window.setTimeout(done, mode === "submit" ? EXIT_MS : 220);
  };

  const named = brandName?.trim() || null;
  const siteOk = !siteError(website);

  // Each settled answer becomes a fact in orbit. Keyed by slot, so clearing a
  // field flies its chip back into the core (AnimatePresence exit) rather than
  // popping it — the universe tracks what you've actually said, both ways.
  const dSite = useDebounced(website, 420);
  const dIndustry = useDebounced(industry, 500);
  const dAbout = useDebounced(about, 600);
  const seeds = useMemo(() => {
    const out: Seed[] = [];
    if (!siteError(dSite)) out.push({ key: "site", label: hostOf(dSite) });
    if (dIndustry.trim()) out.push({ key: "industry", label: shorten(dIndustry, 24) });
    if (dAbout.trim()) out.push({ key: "about", label: shorten(dAbout, 26) });
    return out;
  }, [dSite, dIndustry, dAbout]);

  const goNext = () => {
    const err = siteError(website);
    setError(err);
    if (!err) setStep(1);
  };

  const submit = () => {
    const err = siteError(website);
    if (err) {
      setError(err);
      setStep(0);
      return;
    }
    const message = buildMessage({ brandName: named ?? undefined, website, industry, about });
    leave("submit", () => onSubmit(message));
  };

  const skip = () => leave("skip", onClose);

  // Drives the orbit field. Submitting flares it out; the real stage underneath
  // is already mid-Assembly by the time this clears.
  const level: 0 | 1 | 2 | 3 = leaving === "submit" ? 3 : step === 1 ? 2 : siteOk ? 1 : 0;

  return (
    <ModalOverlay
      isOpen
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      // No isDismissable: the takeover fills the viewport, so a click-outside
      // would only ever be a misclick onto typed answers. Esc still leaves.
      onOpenChange={(open) => {
        if (!open) skip();
      }}
      className="fixed inset-0 z-[var(--z-index-modal)]"
    >
      <RACModal className="h-full w-full outline-none">
        <div className="relative h-full w-full">
          {/* The ground drops in first — the cockpit's own dot-grid, so the
              takeover reads as shell material rather than a sheet on top. */}
          <motion.div
            aria-hidden
            className="co-grid-field absolute inset-0 bg-co-ground"
            initial={{ opacity: 0 }}
            animate={{ opacity: leaving ? 0 : 1 }}
            transition={{
              duration: reduce ? 0.001 : leaving ? 0.3 : 0.25,
              ease: leaving ? EASE_IN : EASE_OUT,
              delay: leaving === "submit" && !reduce ? 0.18 : 0,
            }}
          />

          {/* Chips orbit a full 360°, so they WILL swing toward the text column.
              The mask fades the field out before they reach it — cheaper and
              calmer than choreographing angles that never collide. no-repeat
              matters: the default would tile the gradient across the overflow. */}
          <div aria-hidden className={ORBIT_FRAME} style={ORBIT_FRAME_STYLE}>
            <OrbitField level={level} seeds={seeds} initial={(named ?? "·").charAt(0).toUpperCase()} reduce={reduce} />
          </div>
          <div aria-hidden className={ORBIT_FRAME} style={ORBIT_FRAME_STYLE}>
            <FlareBurst active={level === 3} reduce={reduce} />
          </div>

          <motion.div
            className="relative flex h-full w-full flex-col overflow-y-auto"
            animate={{ opacity: leaving ? 0 : 1, y: leaving && !reduce ? -16 : 0 }}
            transition={{ duration: reduce ? 0.001 : 0.24, ease: EASE_IN }}
          >
            {/* Onboarding: the step ticks at 48/40; Close (40) 28 from the top
                and 40 from the right. */}
            <motion.div
              className="flex flex-none items-center justify-between gap-4 ps-6 pe-4 pt-5 sm:ps-12 sm:pe-10 sm:pt-7"
              {...rise(0.3, reduce, 0)}
            >
              <StepTicks step={step} reduce={reduce} />
              <button
                type="button"
                onClick={skip}
                aria-label="Close onboarding"
                className="grid size-10 flex-none place-items-center rounded-full text-co-text transition-colors hover:bg-co-hover"
              >
                <X className="pointer-events-none size-[18px]" strokeWidth={2} />
              </button>
            </motion.div>

            {/* Form: 520 wide at x120, its top at the middle less 290 (step 1)
                or 330 (step 2), as the frames place it. */}
            <div
              className="flex flex-1 items-start px-6 pb-10 sm:px-10 lg:ps-[120px]"
              style={{ paddingTop: `max(32px, calc(50dvh - ${step === 0 ? 290 : 330}px - 68px))` }}
            >
              <div className="w-full max-w-[520px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    className="flex flex-col"
                    exit={{ opacity: 0, y: reduce ? 0 : -14 }}
                    transition={{ duration: reduce ? 0.001 : 0.22, ease: EASE_IN }}
                  >
                    {step === 0 ? (
                      <>
                        {/* flex-col, not block: RiseLine cancels its own mask
                            padding with negative margins, and as block siblings
                            those margins COLLAPSE (most-negative wins) instead of
                            both applying — which silently pushes the two lines
                            ~0.36em apart. Flex children don't collapse margins. */}
                        <h1 className="flex flex-col font-display text-[clamp(44px,6vw,64px)] font-normal leading-[1.05] tracking-normal text-co-text">
                          {/* Two beats, so the brand's own name lands last. */}
                          <RiseLine delay={0.18} reduce={reduce}>
                            Welcome{named ? "," : "."}
                          </RiseLine>
                          {named ? (
                            <RiseLine delay={0.3} reduce={reduce}>
                              {named}.
                            </RiseLine>
                          ) : null}
                        </h1>

                        <motion.p
                          className="mt-[22px] text-[17px] leading-[1.55] text-co-muted"
                          {...rise(0.62, reduce)}
                        >
                          Your universe is empty: those orbits have nothing in them yet. Every
                          answer puts a fact into one, and the AI takes them as its starting point to
                          research the rest.
                        </motion.p>

                        <motion.div className="mt-[22px]" {...rise(0.74, reduce)}>
                          <KitInput
                            id="onb-website"
                            label="Where does your brand live online?"
                            error={error ?? undefined}
                            value={website}
                            onChange={(e) => {
                              setWebsite(e.target.value);
                              if (error) setError(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") goNext();
                            }}
                            placeholder="yourbrand.com"
                            inputMode="url"
                            autoComplete="url"
                            autoFocus
                          />
                        </motion.div>

                        <motion.div className="mt-[22px] flex items-center gap-5" {...rise(0.86, reduce)}>
                          <KitButton variant="primary" onClick={goNext} className="h-12 px-3.5">
                            Continue
                          </KitButton>
                          <button
                            type="button"
                            onClick={skip}
                            className="h-8 px-1 text-[14px] font-medium text-co-muted transition-colors hover:text-co-text"
                          >
                            Skip
                          </button>
                        </motion.div>
                      </>
                    ) : (
                      <>
                        <h1 className="flex flex-col font-section text-[clamp(36px,4.5vw,44px)] font-extrabold leading-[1.05] tracking-normal text-co-text">
                          <RiseLine delay={0.04} reduce={reduce}>
                            Any other details?
                          </RiseLine>
                        </h1>

                        <motion.p
                          className="mt-[22px] text-[17px] leading-[1.55] text-co-muted"
                          {...rise(0.24, reduce)}
                        >
                          All optional. It just gives the AI a head start. Leave it blank and it'll
                          work from the website alone.
                        </motion.p>

                        <motion.div className="mt-[22px]" {...rise(0.34, reduce)}>
                          <IndustryCombobox value={industry} onChange={setIndustry} />
                        </motion.div>

                        <motion.div className="mt-[22px]" {...rise(0.42, reduce)}>
                          <KitTextarea
                            id="onb-about"
                            label="About your brand"
                            className="min-h-[88px] resize-y"
                            value={about}
                            onChange={(e) => setAbout(e.target.value)}
                            rows={3}
                            placeholder="A sentence or two is plenty. The AI takes it from there."
                          />
                        </motion.div>

                        <motion.div className="mt-[22px] flex items-center gap-3" {...rise(0.5, reduce)}>
                          <KitButton variant="secondary" onClick={() => setStep(0)} className="h-12 flex-none px-3.5">
                            Back
                          </KitButton>
                          <KitButton variant="primary" onClick={submit} className="h-12 flex-none px-3.5">
                            Build universe
                          </KitButton>
                        </motion.div>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </RACModal>
    </ModalOverlay>
  );
}

/** Step ticks: "STEP 1 OF 2" in mono, then two 40px ticks. */
function StepTicks({ step, reduce }: { step: 0 | 1; reduce: boolean }) {
  return (
    <div className="flex items-center gap-2" aria-label={`Step ${step + 1} of 2`}>
      <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-co-text2">Step {step + 1} of 2</span>
      {[0, 1].map((i) => (
        <span key={i} aria-hidden className="block h-[3px] w-10 bg-co-line2">
          <motion.span
            className="block h-full origin-left bg-co-accent"
            initial={false}
            animate={{ scaleX: i <= step ? 1 : 0 }}
            transition={{ duration: reduce ? 0.001 : 0.4, ease: EASE_OUT }}
          />
        </span>
      ))}
    </div>
  );
}
